import { useState, type FormEvent } from "react";
import { submitForm } from "@/lib/forms";
import { analytics, type AnalyticsEvent } from "@/lib/analytics";

type Status = "idle" | "submitting" | "success" | "error";

/**
 * Generic form-submission handler: reads FormData from the form element,
 * checks the honeypot field, runs the caller's validation, and calls
 * submitForm(). Any field named "company_website" is treated as a
 * honeypot and stripped before validation/submission.
 */
export function useFormSubmit<T extends Record<string, string>>(
  validate: (values: T) => Partial<Record<keyof T, string>>,
  event: AnalyticsEvent
) {
  const [status, setStatus] = useState<Status>("idle");
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    // Honeypot: real users never fill this hidden field; bots often do.
    if (formData.get("company_website")) {
      setStatus("success"); // fail silently so bots don't learn it was rejected
      return;
    }

    const values = Object.fromEntries(formData.entries()) as unknown as T;
    delete (values as Record<string, unknown>).company_website;

    const validationErrors = validate(values);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setStatus("error");
      return;
    }

    setStatus("submitting");
    try {
      await submitForm(values);
      analytics.track(event);
      setStatus("success");
      e.currentTarget.reset();
    } catch {
      setStatus("error");
    }
  }

  return { status, errors, handleSubmit };
}
